<!-- Modern Music Account Connection Dashboard -->
<div class="dashboard-container">

  <!-- Header Section -->
  <div class="header-section">
    <h1 class="main-title">{{ CONSTANTS.TEXTS.CONNECT_ACCOUNTS_TITLE }}</h1>
    <p class="main-subtitle">{{ CONSTANTS.TEXTS.CONNECT_ACCOUNTS_SUBTITLE }}</p>

    <!-- Connection Progress -->
    <div class="progress-section">
      <div class="progress-bar">
        <div
          class="progress-fill"
          [style.width.%]="musicSync.connectionProgress()">
        </div>
      </div>
      <p class="progress-text">
        {{ musicSync.connectionStatus() }}
        <span class="progress-percentage">({{ musicSync.connectionProgress() }}%)</span>
      </p>
    </div>
  </div>

  <!-- Connection Cards Grid -->
  <div class="cards-grid">

    <!-- Spotify Connection Card -->
    <div class="connection-card spotify-card"
         [class.connected]="musicSync.spotifyConnected()"
         [class.loading]="musicSync.spotifyLoading()"
         [class.error]="musicSync.spotifyError()">

      <!-- Card Header -->
      <div class="card-header">
        <div class="service-info">
          <div class="service-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#1DB954"/>
              <g fill="#ffffff">
                <path d="M12.5 14.5c1.8-0.3 3.8-0.3 5.6 0 0.3 0.1 0.6-0.1 0.7-0.4s-0.1-0.6-0.4-0.7c-2-0.4-4.2-0.4-6.2 0-0.3 0.1-0.5 0.4-0.4 0.7s0.4 0.5 0.7 0.4z"/>
                <path d="M12.1 16.8c1.6-0.3 3.4-0.3 5 0 0.3 0.1 0.6-0.1 0.7-0.4s-0.1-0.6-0.4-0.7c-1.8-0.3-3.8-0.3-5.6 0-0.3 0.1-0.4 0.4-0.3 0.7 0.1 0.2 0.3 0.4 0.6 0.4z"/>
                <path d="M11.8 19c1.4-0.2 2.9-0.2 4.2 0 0.2 0 0.4-0.1 0.5-0.3s-0.1-0.4-0.3-0.5c-1.5-0.3-3.1-0.3-4.6 0-0.2 0-0.3 0.3-0.3 0.5s0.3 0.3 0.5 0.3z"/>
              </g>
            </svg>
          </div>
          <div class="service-details">
            <h3 class="service-name">Spotify</h3>
            <p class="service-description">{{ CONSTANTS.TEXTS.SPOTIFY_DESCRIPTION }}</p>
          </div>
        </div>

        <!-- Connection Status -->
        <div class="connection-status">
          @if (musicSync.spotifyConnected()) {
            <div class="status-badge connected">
              <span class="checkmark">✓</span>
              {{ CONSTANTS.TEXTS.BUTTONS.CONNECTED }}
            </div>
          } @else if (musicSync.spotifyLoading()) {
            <div class="status-badge loading">
              <div class="spinner"></div>
              {{ CONSTANTS.TEXTS.LOADING_SPOTIFY }}
            </div>
          } @else if (musicSync.spotifyError()) {
            <div class="status-badge error">
              <span class="error-icon">⚠</span>
              Error
            </div>
          } @else {
            <div class="status-badge disconnected">
              Not Connected
            </div>
          }
        </div>
      </div>

      <!-- Connected User Info -->
      @if (musicSync.spotifyConnected() && musicSync.spotifyUser()) {
        <div class="user-info">
          <div class="user-avatar">
            @if (getSpotifyUserImage()) {
              <img [src]="getSpotifyUserImage()" [alt]="musicSync.spotifyUser()?.display_name">
            } @else {
              <div class="avatar-placeholder">🎵</div>
            }
          </div>
          <div class="user-details">
            <h4>{{ musicSync.spotifyUser()?.display_name }}</h4>
            <p>Connected and ready for library sync</p>
          </div>
        </div>
      }

      <!-- Action Buttons -->
      <div class="card-actions">
        @if (!musicSync.spotifyConnected()) {
          <button
            class="connect-btn spotify-btn"
            (click)="connectSpotify()"
            [disabled]="musicSync.spotifyLoading()">
            {{ CONSTANTS.TEXTS.BUTTONS.CONNECT_SPOTIFY }}
          </button>
        } @else {
          <div class="connected-actions">
            <button
              class="refresh-btn"
              (click)="refreshSpotify()"
              [disabled]="musicSync.spotifyLoading()">
              🔄 Refresh
            </button>
            <button
              class="disconnect-btn"
              (click)="disconnectSpotify()">
              {{ CONSTANTS.TEXTS.BUTTONS.DISCONNECT }}
            </button>
          </div>
        }
      </div>

      <!-- Debug Toggle -->
      <div class="debug-toggle">
        <button
          class="debug-btn"
          (click)="toggleSpotifyDebug()">
          {{ showSpotifyDebug ? '🔽' : '▶️' }} Debug Data
        </button>
      </div>

      <!-- Debug Panel -->
      @if (showSpotifyDebug) {
        <div class="debug-panel">
          <!-- Library Sync Controls -->
          @if (musicSync.spotifyConnected()) {
            <div class="sync-controls">
              @if (!musicSync.spotifyLibrarySyncLoading()) {
                <button
                  class="sync-library-btn"
                  (click)="syncSpotifyLibraryDebug()">
                  🔄 Sync Library (Stats-Only Mode)
                </button>
                <small class="sync-mode-info">
                  🔒 Large libraries automatically use stats-only mode to prevent browser crashes
                </small>
              } @else {
                <div class="sync-active-controls">
                  <div class="sync-status">
                    <div class="sync-spinner"></div>
                    <span>Syncing Library...</span>
                  </div>
                  <button
                    class="cancel-sync-btn"
                    (click)="cancelSpotifyLibrarySync()">
                    🛑 Cancel
                  </button>
                </div>
              }
            </div>

            <!-- Phase Timeline -->
            @if (musicSync.spotifyLibrarySyncLoading()) {
              <div class="phase-timeline">
                <h5>📋 Sync Phases</h5>
                <div class="timeline-container">
                  @for (phase of getPhaseList(); track phase.type) {
                    <div class="timeline-item"
                         [class.completed]="phase.completed"
                         [class.current]="phase.current">
                      <div class="timeline-icon">{{ phase.icon }}</div>
                      <div class="timeline-label">{{ phase.name }}</div>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Loading Progress -->
            @if (musicSync.spotifyLibrarySyncLoading()) {
              <div class="sync-progress">
                <div class="progress-header">
                  <h5>🔄 Syncing Spotify Library</h5>
                  <span class="progress-percentage">{{ musicSync.spotifyLibrarySyncProgress().toFixed(1) }}%</span>
                </div>

                <div class="progress-bar">
                  <div class="progress-fill" [style.width.%]="musicSync.spotifyLibrarySyncProgress()"></div>
                </div>

                <div class="progress-details">
                  <p class="current-phase">{{ musicSync.spotifyLibrarySyncPhase() }}</p>
                  @if (musicSync.spotifyLibrarySyncCurrent() > 0 && musicSync.spotifyLibrarySyncTotal() > 0) {
                    <p class="progress-counts">📊 {{ musicSync.spotifyLibrarySyncCurrent() }} / {{ musicSync.spotifyLibrarySyncTotal() }}</p>
                  }
                  <p class="elapsed-time">⏱️ {{ syncElapsedTime }}s elapsed</p>
                </div>

                <div class="progress-tip">
                  <small>💡 Large libraries (7000+ tracks) may take 5-10 minutes</small>
                </div>
              </div>
            }

            <!-- Error Display -->
            @if (musicSync.spotifyLibrarySyncError()) {
              <div class="sync-error">
                <span class="error-icon">⚠️</span>
                {{ musicSync.spotifyLibrarySyncError() }}
              </div>
            }

            <!-- Stats Display -->
            @if (getSpotifyLibrarySyncStats()) {
              <div class="sync-stats">
                <h5>📊 Sync Statistics</h5>
                <div class="stats-grid">
                  <div class="stat-item">
                    <span class="stat-label">User:</span>
                    <span class="stat-value">{{ getSpotifyLibrarySyncStats()?.userName }} ({{ getSpotifyLibrarySyncStats()?.userId }})</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Liked Tracks:</span>
                    <span class="stat-value">{{ getSpotifyLibrarySyncStats()?.totalLikedTracks | number }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Saved Albums:</span>
                    <span class="stat-value">{{ getSpotifyLibrarySyncStats()?.totalSavedAlbums | number }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Playlists:</span>
                    <span class="stat-value">{{ getSpotifyLibrarySyncStats()?.totalPlaylists | number }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Unique Artists:</span>
                    <span class="stat-value">{{ getSpotifyLibrarySyncStats()?.totalUniqueArtists | number }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Sync Duration:</span>
                    <span class="stat-value">{{ getSpotifyLibrarySyncStats()?.syncDurationMs | number }}ms</span>
                  </div>
                </div>
              </div>
            }
          }

          <!-- Stats-Only Mode Notice -->
          @if (isStatsOnlyMode()) {
            <div class="stats-only-notice">
              <h5>📊 Stats-Only Mode Active</h5>
              <p>{{ getStatsOnlyMessage() }}</p>
              <div class="stats-only-details">
                <p><strong>✅ Full Sync Completed Successfully!</strong></p>
                <p>All your music data was processed and synced. Only statistics are displayed to prevent browser crashes with very large libraries (7000+ tracks).</p>
                <small>🔒 This mode is automatically activated for libraries that would cause memory issues when displaying full track lists.</small>
              </div>
            </div>
          }

          <!-- Data Truncation Warning (for smaller libraries) -->
          @if (isDataTruncated() && !isStatsOnlyMode()) {
            <div class="truncation-warning">
              <h5>⚠️ Large Dataset Notice</h5>
              <p>Your library is very large! For performance reasons, only a preview of your data is shown below:</p>
              <div class="truncation-details">
                <p><strong>Total Items:</strong></p>
                <ul>
                  <li>Liked Tracks: {{ getDataTruncationInfo()?.originalCounts?.likedTracks | number }} (showing {{ getDataTruncationInfo()?.showing?.likedTracks }})</li>
                  <li>Saved Albums: {{ getDataTruncationInfo()?.originalCounts?.savedAlbums | number }} (showing {{ getDataTruncationInfo()?.showing?.savedAlbums }})</li>
                  <li>Playlists: {{ getDataTruncationInfo()?.originalCounts?.playlists | number }} (showing {{ getDataTruncationInfo()?.showing?.playlists }})</li>
                </ul>
                <small>Complete statistics are shown above. Full sync was successful!</small>
              </div>
            </div>
          }

          <!-- Standard Debug Data -->
          <div class="debug-data">
            <h5>🔍 Debug Information</h5>
            <pre>{{ getSpotifyDebugData() | json }}</pre>
          </div>

          <!-- Full JSON Response (if library sync was executed) -->
          @if (getSpotifyLibrarySyncFullData()) {
            <div class="full-response">
              <h5>📋 Full Sync Response</h5>
              <pre>{{ getSpotifyLibrarySyncFullData() | json }}</pre>
            </div>
          }
        </div>
      }
    </div>

    <!-- Discogs Connection Card -->
    <div class="connection-card discogs-card"
         [class.connected]="musicSync.discogsConnected()"
         [class.loading]="musicSync.discogsLoading()"
         [class.error]="musicSync.discogsError()">

      <!-- Card Header -->
      <div class="card-header">
        <div class="service-info">
          <div class="service-logo">
            <img
              src="assets/discogs-logo.png"
              alt="Discogs"
              width="32"
              height="32"
              style="border-radius: 4px;">
          </div>
          <div class="service-details">
            <h3 class="service-name">Discogs</h3>
            <p class="service-description">{{ CONSTANTS.TEXTS.DISCOGS_DESCRIPTION }}</p>
          </div>
        </div>

        <!-- Connection Status -->
        <div class="connection-status">
          @if (musicSync.discogsConnected()) {
            <div class="status-badge connected">
              <span class="checkmark">✓</span>
              {{ CONSTANTS.TEXTS.BUTTONS.CONNECTED }}
            </div>
          } @else if (musicSync.discogsLoading()) {
            <div class="status-badge loading">
              <div class="spinner"></div>
              {{ CONSTANTS.TEXTS.LOADING_DISCOGS }}
            </div>
          } @else if (musicSync.discogsError()) {
            <div class="status-badge error">
              <span class="error-icon">⚠</span>
              Error
            </div>
          } @else {
            <div class="status-badge disconnected">
              Not Connected
            </div>
          }
        </div>
      </div>

      <!-- Connected User Info -->
      @if (musicSync.discogsConnected() && musicSync.discogsUser()) {
        <div class="user-info">
          <div class="user-avatar">
            @if (getDiscogsUserImage()) {
              <img [src]="getDiscogsUserImage()" [alt]="musicSync.discogsUser()?.username">
            } @else {
              <div class="avatar-placeholder">🎧</div>
            }
          </div>
          <div class="user-details">
            <h4>{{ musicSync.discogsUser()?.username }}</h4>
            <p>{{ musicSync.discogsUser()?.num_collection }} collection • {{ musicSync.discogsWishlist().length }} wishlist</p>
          </div>
        </div>
      }

      <!-- Action Buttons -->
      <div class="card-actions">
        @if (!musicSync.discogsConnected()) {
          <button
            class="connect-btn discogs-btn"
            (click)="connectDiscogs()"
            [disabled]="musicSync.discogsLoading()">
            {{ CONSTANTS.TEXTS.BUTTONS.CONNECT_DISCOGS }}
          </button>
        } @else {
          <div class="connected-actions">
            <button
              class="refresh-btn"
              (click)="refreshDiscogs()"
              [disabled]="musicSync.discogsLoading()">
              🔄 Refresh
            </button>
            <button
              class="disconnect-btn"
              (click)="disconnectDiscogs()">
              {{ CONSTANTS.TEXTS.BUTTONS.DISCONNECT }}
            </button>
          </div>
        }
      </div>

      <!-- Debug Toggle -->
      <div class="debug-toggle">
        <button
          class="debug-btn"
          (click)="toggleDiscogsDebug()">
          {{ showDiscogsDebug ? '🔽' : '▶️' }} Debug Data
        </button>
      </div>

      <!-- Debug Panel -->
      @if (showDiscogsDebug) {
        <div class="debug-panel">
          <pre>{{ getDiscogsDebugData() | json }}</pre>
        </div>
      }
    </div>
  </div>

  <!-- Success State -->
  @if (musicSync.bothConnected()) {
    <div class="success-section">
      <div class="success-card">
        <div class="success-icon">🎉</div>
        <h3>{{ CONSTANTS.TEXTS.EXCELLENT_TITLE }}</h3>
        <p>{{ CONSTANTS.TEXTS.BOTH_CONNECTED_DESCRIPTION }}</p>
        <button
          class="continue-btn"
          (click)="router.navigate([CONSTANTS.ROUTES.DASHBOARD_OVERVIEW])">
          {{ CONSTANTS.TEXTS.CONTINUE_TO_APP }}
        </button>
      </div>
    </div>
  }

</div>
